/**
 * Pieces the WS-4 ticket screens share (copied from Well Testing's
 * TicketBits; this vertical's API mirrors WT-4): the reverted-to-draft notice, the
 * money cell, and the rate-card option text.
 *
 * NOTHING HERE ADDS UP. A line's amount, the draft subtotal, the tax and the
 * total are the API's figures; an absent one renders `—`.
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { fmtCurrency } from '../../../lib/display-labels';
import { wsRateUnitSuffix, WS_REVERTED_NOTICE } from '../../../lib/ws-tickets';
import type { WsRate } from '../../../types/ws-tickets';

/**
 * Said when a write comes back `reverted_to_draft: true`. A warning, not a
 * success and not a refusal: amber edge, role="status", stays until dismissed.
 */
export function WsRevertedNotice({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div
      role="status"
      data-testid="ws-ticket-reverted"
      className="flex items-start gap-3 rounded-fi-control border border-fi-amber/60 bg-fi-signal-amber/10 px-4 py-3 text-sm text-fieldiq-text"
    >
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-fi-signal-amber" aria-hidden="true" />
      <p className="flex-1">{WS_REVERTED_NOTICE}</p>
      <button type="button" className="text-xs font-bold underline" onClick={onDismiss}>
        Dismiss
      </button>
    </div>
  );
}

/**
 * The visible "unpriced" flag: a ticket (or a line) with no rate is never a
 * $0 — its money cell reads `—` and this says why. `count` is the API's
 * `unpricedCount` on a ticket; a line passes 1.
 */
export function WsUnpricedFlag({ count, testId = 'ws-unpriced-flag' }: { count: number | null | undefined; testId?: string }) {
  if (!count || count < 1) return null;
  return (
    <span
      data-testid={testId}
      className="ml-2 inline-flex items-center gap-1 rounded-fi-control border border-fi-amber/60 bg-fi-signal-amber/10 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-fi-signal-amber"
      title={count === 1 ? 'One line has no rate' : `${count} lines have no rate`}
    >
      <AlertTriangle className="size-3" aria-hidden="true" />
      {count === 1 ? 'Unpriced' : `${count} unpriced`}
    </span>
  );
}

/** A rate as `CA$450.00/day`; an absent rate is `—`. */
export function wsRateText(
  rate: number | null | undefined,
  currency: string | null | undefined,
  unit: string | null | undefined,
  distanceUnit?: string | null,
  volumeUnit?: string | null,
) {
  const v = fmtCurrency(rate, currency);
  return v === '—' ? v : `${v}${wsRateUnitSuffix(unit, distanceUnit, volumeUnit)}`;
}

/**
 * What a rate charges, as text. A third-party rate has no rate of its own —
 * it says how the vendor's cost is billed (`At cost`, `Cost + 15%`).
 */
export function wsRatePriceText(r: Pick<WsRate, 'kind' | 'rate' | 'currency' | 'unit' | 'distanceUnit' | 'volumeUnit' | 'pricing' | 'markupPct'>) {
  if (r.kind === 'third_party' || r.rate === null) {
    if (r.pricing === 'markup_pct') return r.markupPct === null ? 'Cost + markup' : `Cost + ${r.markupPct}%`;
    if (r.pricing === 'at_cost') return 'At cost';
    return '—';
  }
  return wsRateText(r.rate, r.currency, r.unit, r.distanceUnit, r.volumeUnit);
}

/** The rate-card option text: `RIG-HR · Rig time — CA$650.00/hr (Default card)`. */
export function wsRateOption(r: WsRate) {
  const who = r.customerId ? r.customerName || 'Customer card' : 'Default card';
  return `${r.code} · ${r.name} — ${wsRatePriceText(r)} (${who})`;
}

/**
 * The rates a ticket's line may be priced from: this ticket's customer or the
 * default card, in the ticket's currency, live on the day (when known). The API
 * checks all of it again (`rate_other_customer`, `rate_other_currency`).
 */
export function ratesForTicket(
  rates: WsRate[],
  t: { customerId: string | null; currency: string | null },
  opts: { unit?: string | null; kind?: string | null; on?: string | null } = {},
) {
  return rates.filter(
    (r) =>
      (r.customerId === null || r.customerId === t.customerId) &&
      (!t.currency || !r.currency || r.currency === t.currency) &&
      (!opts.unit || r.unit === opts.unit) &&
      (!opts.kind || r.kind === opts.kind) &&
      (!opts.on || (r.effectiveFrom <= opts.on && (!r.effectiveTo || r.effectiveTo >= opts.on))),
  );
}
